import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { API_ENDPOINTS } from '../../config/apiRouting';
import './PaymentCheckout.css';

interface PaymentLink {
  paymentId: string;
  checkId: string;
  storeId: string;
  totalAmount: number;
  tipAmount: number;
  items: any[];
  serverName: string;
  tableNumber: string;
  status: string;
}

interface PaymentFormProps {
  paymentLink: PaymentLink;
  onSuccess: (tipAmount: number) => void;
}

const PaymentForm: React.FC<PaymentFormProps> = ({ paymentLink, onSuccess }) => {
  const [searchParams] = useSearchParams();
  const [tipAmount, setTipAmount] = useState(0);
  const [tipPercentage, setTipPercentage] = useState(18);
  const [customTip, setCustomTip] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  
  // Credit card form fields
  const [cardNumber, setCardNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [cardholderName, setCardholderName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');

  const tipOptions = [
    { percentage: 15, label: '15%' },
    { percentage: 18, label: '18%' },
    { percentage: 20, label: '20%' },
    { percentage: 25, label: '25%' }
  ];

  useEffect(() => {
    // Calculate initial tip
    const calculatedTip = paymentLink.totalAmount * (tipPercentage / 100);
    setTipAmount(Number(calculatedTip.toFixed(2)));
  }, [paymentLink.totalAmount, tipPercentage]);

  const handleTipSelection = (percentage: number) => {
    setTipPercentage(percentage);
    setCustomTip('');
  };

  const handleCustomTipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCustomTip(value);
    const numValue = parseFloat(value) || 0;
    setTipAmount(Number(numValue.toFixed(2)));
    setTipPercentage(0);
  };

  const processPayment = async () => {
    const token = searchParams.get('token');

    // Parse expiry date
    const [expiryMonth, expiryYear] = expiryDate.split('/');

    try {
      const response = await fetch(API_ENDPOINTS.paymentProcess(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          paymentId: paymentLink.paymentId,
          token: token,
          cardNumber: cardNumber.replace(/\s/g, ''), // Remove spaces
          expiryMonth: expiryMonth,
          expiryYear: expiryYear,
          cvv: cvv,
          cardholderName: cardholderName,
          tipAmount: tipAmount,
          customerEmail: customerEmail,
          customerPhone: ''
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Payment failed');
      }

      return await response.json();
    } catch (error) {
      console.error('Payment error:', error);
      throw error;
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    // Validate form fields
    if (!cardNumber || !expiryDate || !cvv || !cardholderName) {
      setError('Please fill in all card details');
      return;
    }

    // Validate card number (basic check)
    if (cardNumber.replace(/\s/g, '').length < 13) {
      setError('Please enter a valid card number');
      return;
    }

    // Validate expiry date format
    if (!/^\d{2}\/\d{2}$/.test(expiryDate)) {
      setError('Please enter expiry date in MM/YY format');
      return;
    }

    // Validate CVV
    if (cvv.length < 3) {
      setError('Please enter a valid CVV');
      return;
    }

    setIsProcessing(true);
    setError('');

    try {
      const result = await processPayment();
      if (result.success) {
        onSuccess(tipAmount);
      } else {
        setError(result.error || 'Payment failed');
      }
    } catch (err: any) {
      setError(err.message || 'Payment processing error');
    }

    setIsProcessing(false);
  };

  // Format card number with spaces
  const formatCardNumber = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = matches && matches[0] || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    if (parts.length) {
      return parts.join(' ');
    } else {
      return v;
    }
  };

  // Format expiry date
  const formatExpiryDate = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    if (v.length >= 2) {
      return v.substring(0, 2) + '/' + v.substring(2, 4);
    }
    return v;
  };

  const finalAmount = paymentLink.totalAmount + tipAmount;

  return (
    <form onSubmit={handleSubmit} className="payment-form">
      <div className="order-summary">
        <h3>Order Summary</h3>
        <div className="summary-row">
          <span>Subtotal</span>
          <span>${paymentLink.totalAmount.toFixed(2)}</span>
        </div>
        <div className="summary-row tip-row">
          <span>Tip</span>
          <span>${tipAmount.toFixed(2)}</span>
        </div>
        <div className="summary-row total-row">
          <span>Total</span>
          <span>${finalAmount.toFixed(2)}</span>
        </div>
      </div>

      <div className="tip-section">
        <h3>Add Tip</h3>
        <div className="tip-options">
          {tipOptions.map(option => (
            <button
              key={option.percentage}
              type="button"
              className={`tip-button ${tipPercentage === option.percentage ? 'active' : ''}`}
              onClick={() => handleTipSelection(option.percentage)}
            >
              {option.label}
            </button>
          ))}
          <div className="custom-tip">
            <input
              type="number"
              step="0.01"
              placeholder="Custom $"
              value={customTip}
              onChange={handleCustomTipChange}
              className="custom-tip-input"
            />
          </div>
        </div>
      </div>

      <div className="customer-section">
        <h3>Contact Information</h3>
        <input
          type="email"
          placeholder="Email address (optional)"
          value={customerEmail}
          onChange={(e) => setCustomerEmail(e.target.value)}
          className="customer-input"
        />
      </div>

      <div className="card-section">
        <h3>Payment Details</h3>
        <div className="card-form">
          <input
            type="text"
            placeholder="Card Number"
            value={cardNumber}
            onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
            maxLength={19}
            className="card-input"
          />
          
          <div className="card-row">
            <input
              type="text"
              placeholder="MM/YY"
              value={expiryDate}
              onChange={(e) => setExpiryDate(formatExpiryDate(e.target.value))}
              maxLength={5}
              className="card-input card-input-small"
            />
            <input
              type="text"
              placeholder="CVV"
              value={cvv}
              onChange={(e) => setCvv(e.target.value.replace(/[^0-9]/g, ''))}
              maxLength={4}
              className="card-input card-input-small"
            />
          </div>
          
          <input
            type="text"
            placeholder="Cardholder Name"
            value={cardholderName}
            onChange={(e) => setCardholderName(e.target.value)}
            className="card-input"
          />
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={isProcessing}
        className="pay-button"
      >
        {isProcessing ? 'Processing...' : `Pay $${finalAmount.toFixed(2)}`}
      </button>
    </form>
  );
};

const PaymentCheckout: React.FC = () => {
  const { paymentId } = useParams<{ paymentId: string }>();
  const [searchParams] = useSearchParams();
  const [paymentLink, setPaymentLink] = useState<PaymentLink | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [finalTipAmount, setFinalTipAmount] = useState(0);

  useEffect(() => {
    validatePaymentLink();
  }, [paymentId]);

  const validatePaymentLink = async () => {
    const token = searchParams.get('token');
    
    console.log('PaymentCheckout: Starting validation', { paymentId, token, apiUrl: API_ENDPOINTS.paymentLinks() });
    
    if (!paymentId) {
      console.error('PaymentCheckout: No payment ID found in URL');
      setError('Invalid payment link');
      setLoading(false);
      return;
    }
    
    if (!token) {
      console.error('PaymentCheckout: No token found in URL');
      setError('Invalid payment link');
      setLoading(false);
      return;
    }

    try {
      const validateUrl = `${API_ENDPOINTS.paymentLink(paymentId)}/validate?token=${token}`;
      console.log('PaymentCheckout: Fetching', validateUrl);
      
      const response = await fetch(validateUrl);
      console.log('PaymentCheckout: Response status', response.status);

      if (!response.ok) {
        const data = await response.json();
        console.error('PaymentCheckout: Validation error', data);
        setError(data.error || 'Invalid payment link');
        setLoading(false);
        return;
      }

      const data = await response.json();
      console.log('PaymentCheckout: Payment link validated', data);
      setPaymentLink(data);
      setLoading(false);
    } catch (err) {
      console.error('PaymentCheckout: Fetch error', err);
      setError('Failed to load payment details');
      setLoading(false);
    }
  };

  const handlePaymentSuccess = (tipAmount: number) => {
    setFinalTipAmount(tipAmount);
    setSuccess(true);
  };

  if (loading) {
    return (
      <div className="payment-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading payment details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payment-container">
        <div className="error-container">
          <h2>Payment Error</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="payment-container">
        <div className="success-container">
          <div className="success-icon">✓</div>
          <h2>Payment Successful!</h2>
          <p>Thank you for dining with us!</p>
          <div className="receipt-summary">
            <p>Total: ${paymentLink?.totalAmount.toFixed(2)}</p>
            <p>Tip: ${finalTipAmount.toFixed(2)}</p>
            <p className="final-total">
              Final Amount: ${((paymentLink?.totalAmount || 0) + finalTipAmount).toFixed(2)}
            </p>
          </div>
          <p className="server-info">Your server {paymentLink?.serverName} has been notified.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-container">
      <div className="payment-header">
        <h1>BigSystemOne</h1>
        <p>Table {paymentLink?.tableNumber} • Server: {paymentLink?.serverName}</p>
      </div>
      
      {paymentLink && (
        <PaymentForm 
          paymentLink={paymentLink} 
          onSuccess={handlePaymentSuccess}
        />
      )}
    </div>
  );
};

export default PaymentCheckout;